import { AccessController, serializeObject } from "shared/utils";
import { useRequestStore } from "../store/useRequestStore";
import { useRequestPersistedStore } from "../store/useRequestPersistedStore";
import { userStore } from "entities/User/store/userStore";
import {
  expenseRequestStatusesTable,
  travelRequestStatusesTable,
  eurocardRequestStatusesTable,
  creditcardRequestStatusesTable,
} from "../constants/statusesTables";
import { REQUEST_STATUSES, REQUEST_TYPES, ROLES } from "shared/config";
import { getCostLineDecisions, useCostLinesStore } from "entities/CostLines";
import { costCentresWithExpenseDoctypes } from "../constants";

export class RequestAccessController extends AccessController {
  constructor() {
    super();
    this.statusesTable = null;
    this.withCostLineValidation = this.#validateModuleSpecificMethod(
      "CostLine",
      ["edit", "delete", "viewExpenseDoctype", "editExpenseDoctype"],
      this.withCostLineValidation.bind(this)
    );
  }

  to(module, action) {
    this.module = module;
    this.action = action;
    this.type = useRequestStore?.getState().request?.type;
    this.status = useRequestStore?.getState().request?.status;
    this.role = useRequestPersistedStore?.getState().viewRequestAs.role;
    this.cost_centre = useRequestStore?.getState().request?.cost_centre;
    this.#updateStatusesTable(this.type);
    this.providesAccess =
      this.statusesTable?.[module]?.[action]?.[this.role]?.includes(
        this.status
      ) ?? false;
    return this;
  }

  forCountries(countries) {
    if (this.providesAccess) {
      const userCountry = userStore?.getState().user?.country_code;
      this.providesAccess = countries.includes(userCountry);
    }
    return this;
  }

  forCostCentres(costCentres) {
    if (this.providesAccess) {
      this.providesAccess = costCentres.some((costCentre) =>
        this.cost_centre
          ?.slice(0, costCentre.length - 1)
          .includes(costCentre.slice(0, costCentre.length - 1))
      );
    }
    return this;
  }

  withLock() {
    const requestLock = useRequestStore?.getState().request?.locks;
    if (requestLock && this.providesAccess) {
      const lock = serializeObject(requestLock);
      if (lock === null) {
        this.providesAccess = true;
        return;
      }

      const userId = userStore?.getState().user?.id;
      const lockHolderId = lock.data.holder_id;

      this.providesAccess = userId === lockHolderId;
    }
    return this;
  }

  withHold() {
    const requestOnHold = useRequestStore?.getState().request?.onHold;
    if (requestOnHold && this.providesAccess) {
      const onHold = serializeObject(requestOnHold.data);
      this.providesAccess = onHold === null;
    }
    return this;
  }

  withCostLineValidation(costLineId) {
    if (this.providesAccess) {
      const costLine = useCostLinesStore
        ?.getState()
        .costLines.find((costLine) => costLine.id === costLineId);

      const { isAccepted, isRejected } = getCostLineDecisions(
        costLine,
        this.role
      );

      const acceptorRole = costLine?.acceptations?.data?.acceptor_role;

      this.providesAccess = !isAccepted || isRejected;

      if (this.action === "delete") {
        this.providesAccess =
          (this.status === REQUEST_STATUSES.AWAITING_USER_SETTLEMENT &&
            this.type === REQUEST_TYPES.TRAVEL &&
            !costLine.accommodation_required_line) ||
          (useCostLinesStore?.getState().costLines.length > 1 &&
            !costLine.accommodation_required_line);
      }

      if (this.role === ROLES.ACCOUNTS_PAYABLE) {
        this.providesAccess =
          costLine?.cost_type?.data?.subtype !== "Mileage" &&
          !isAccepted &&
          acceptorRole !== ROLES.COST_GROUP;
      }

      if (this.action === "viewExpenseDoctype") {
        this.providesAccess = this.to("CostLine", "viewExpenseDoctype")
          .forCostCentres(costCentresWithExpenseDoctypes)
          .provided();
      }

      if (this.action === "editExpenseDoctype") {
        if (
          this.to("CostLine", "viewExpenseDoctype")
            .withCostLineValidation(costLine.id)
            .provided()
        ) {
          this.providesAccess =
            (acceptorRole !== ROLES.ACCOUNTS_PAYABLE && isAccepted) ||
            !isAccepted;
        } else {
          this.providesAccess = false;
        }
      }
    }
    return this;
  }

  provided() {
    return this.providesAccess;
  }
  unprovided() {
    return !this.providesAccess;
  }

  #validateModuleSpecificMethod(module, actions, method) {
    return function (args) {
      if (
        this.module !== module ||
        (actions !== "*" && !actions.includes(this.action))
      ) {
        throw new Error(
          `${
            method.name
          } method can only be used with ${module} module and ${actions.join(
            ", "
          )} action(s).`
        );
      } else return method(args);
    };
  }

  #updateStatusesTable(type) {
    if (type === REQUEST_TYPES.TRAVEL)
      this.statusesTable = travelRequestStatusesTable;
    if (type === REQUEST_TYPES.EXPENSE)
      this.statusesTable = expenseRequestStatusesTable;
    if (type === REQUEST_TYPES.EUROCARD)
      this.statusesTable = eurocardRequestStatusesTable;
    if (type === REQUEST_TYPES.CREDIT_CARD || type === REQUEST_TYPES.AMEX_BTA)
      this.statusesTable = creditcardRequestStatusesTable;
  }
}
